import React, { useState, useEffect, useRef } from 'react';
import { supabase } from "../supabaseClient";
import axios from "axios";
import { LayoutDashboard, GraduationCap, ClipboardList, CalendarDays, Landmark, User, CreditCard, TrendingUp } from 'lucide-react';
import BankingInfoSection from './BankingInfoSection';
import '../styles/ModernDashboard.css';
import AvailabilityManagement from './AvailabilityManagement';
import ReservationsDashboard from './ReservationsDashboard';
import MesCoursProf from '../pages/MesCoursProf';
import OnboardingGuide from './OnboardingGuide';
import { profOnboardingSteps } from './onboardingSteps';
import FicheEleveProgression from './learning/FicheEleveProgression';

// Composant d'upload de photo intégré
const PhotoUploadComponent = ({ profProfile, token, onPhotoUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  // URL complète de la photo ou avatar par défaut
  const getPhotoUrl = () => {
    if (profProfile?.photo_url) {
      // Si l'URL commence par http, c'est une URL complète
      if (profProfile.photo_url.startsWith('http')) {
        return profProfile.photo_url;
      }
      // Sinon, c'est un chemin relatif vers notre serveur
      return `${window.location.origin}${profProfile.photo_url}`;
    }
    return null;
  };

  // Gérer la sélection de fichier
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation du fichier
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image doit faire moins de 5MB');
      return;
    }

    // Créer un aperçu
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload automatique
    handleUpload(file);
  };

  // Upload de la photo
  const handleUpload = async (file) => {
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/profs/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        
        // Callback pour mettre à jour le profil parent
        if (onPhotoUpdate) {
          onPhotoUpdate(data.photo_url);
        }

        // Réinitialiser le preview
        setPreview(null);
        
        // Message de succès
        alert('Photo mise à jour avec succès !');
      } else {
        console.error('❌ Erreur upload:', data.error);
        alert(`Erreur: ${data.error}`);
        setPreview(null);
      }
    } catch (error) {
      console.error('❌ Erreur upload:', error);
      alert('Erreur lors de l\'upload de la photo');
      setPreview(null);
    } finally {
      setUploading(false);
      // Réinitialiser l'input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="avatar-section">
      <div className="modern-avatar-container group">
        {/* Avatar actuel ou preview */}
        <div className="modern-avatar">
          {preview ? (
            // Preview de la nouvelle image
            <img 
              src={preview} 
              alt="Preview" 
              className="avatar-image"
            />
          ) : getPhotoUrl() ? (
            // Photo existante
            <img 
              src={getPhotoUrl()} 
              alt={`Photo de ${profProfile?.prenom || profProfile?.nom}`}
              className="avatar-image"
              onError={(e) => {
                // Si l'image ne charge pas, afficher les initiales
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          
          {/* Initiales par défaut si pas de photo */}
          <div 
            className={`avatar-initials ${getPhotoUrl() && !preview ? 'hidden' : ''}`}
          >
            {profProfile?.prenom?.[0]?.toUpperCase() || profProfile?.nom?.[0]?.toUpperCase() || 'P'}
          </div>

          {/* Overlay avec icône upload */}
          <div className="avatar-overlay">
            {uploading ? (
              <div className="upload-spinner"></div>
            ) : (
              <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </div>

          {/* Input file caché */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="avatar-file-input"
            disabled={uploading}
          />
        </div>

        <div className="online-indicator"></div>
      </div>
    </div>
  );
};

const ModernProfDashboard = () => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [profProfile, setProfProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  
  // Stats adaptées de votre dashboard actuel
  const [stats, setStats] = useState({
    totalCours: 0,
    coursAVenir: 0,
    coursFinis: 0,
    evaluations: 0,
    noteMoyenne: 0,
    revenus: 0
  });

  const [prochainsCours, setProchainsCours] = useState([]);
  const [mesEleves, setMesEleves] = useState([]); // Liste unique des élèves (passés + à venir) → fiche accessible à tout moment
  const [observableCourses, setObservableCourses] = useState(null); // 👁 null = non-admin ; tableau = admin (prof pédagogique)
  const [ficheEleveOpen, setFicheEleveOpen] = useState(false);
  const [ficheEleveData, setFicheEleveData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const ticker = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(ticker);
  }, []);

  // Auto-refresh données toutes les 30 secondes
  useEffect(() => {
    if (!profProfile || !token) return;
    const interval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        clearInterval(interval);
        return;
      }
      loadData(profProfile, session.access_token);
    }, 30000);
    return () => clearInterval(interval);
  }, [profProfile, token]);
  const [recentEvaluations, setRecentEvaluations] = useState([]);
  
  // Données de payouts (comme dans votre dashboard actuel)
  const [payoutData, setPayoutData] = useState({
    current_month: {
      courses: 0,
      gross_amount: 0,
      net_amount: 0,
      status: 'pending'
    },
    total_lifetime: {
      courses: 0,
      net_amount: 0
    },
    pending_payout: 0,
    next_payout_date: null,
    last_payout: null
  });

  const [isVisible, setIsVisible] = useState(false);

  // Logs de debug pour identifier les re-renders
  useEffect(() => {
  }, [activeTab, prochainsCours, loading, isVisible]);

  useEffect(() => {
    let isInitialized = false;
    
    const initApp = async () => {
      if (isInitialized) return;
      isInitialized = true;
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // Jeton absent/expiré → pas d'accès au dashboard, retour à l'accueil
          window.location.href = '/';
          return;
        }

        // Un non-prof (élève / admin) ne doit pas voir le dashboard prof
        const role = session.user?.user_metadata?.role;
        if (role && role !== 'prof') {
          window.location.href = '/dashboard';
          return;
        }

        setSession(session);
        setUser(session.user);
        setToken(session.access_token);

        await loadProfile(session.access_token);
        setIsVisible(true);
        
      } catch (error) {
        console.error("Erreur init:", error);
        setLoading(false);
      }
    };

    initApp();
    
    return () => {
      isInitialized = false;
    };
  }, []);

  // Fonction loadProfile adaptée de votre dashboard actuel
  const loadProfile = async (accessToken) => {
    try {
      const profResponse = await axios.get(`${process.env.REACT_APP_API_URL}/profs/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (profResponse.data.success && profResponse.data.prof) {
        setProfProfile(profResponse.data.prof);
        await loadData(profResponse.data.prof, accessToken);
      } else {
        // Profil non trouvé — il sera créé via pre-register lors de la prochaine connexion
        console.log('ℹ️ Profil prof non trouvé en DB');
        setLoading(false);
      }
      
    } catch (err) {
      console.error("Erreur profil:", err);
      setLoading(false);
    }
  };

  // Fonction loadPayoutData adaptée
  const loadPayoutData = async (accessToken) => {
    try {
      const payoutResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/payouts/professor/current`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (payoutResponse.data.success) {
        setPayoutData(payoutResponse.data.data);
      }
      
    } catch (error) {
    }
  };

  // Fonction loadData complète adaptée de votre dashboard AVEC CORRECTIONS
  const loadData = async (profData, accessToken) => {
    try {
      const reservationsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/booking/reservations/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });


      let reservationsData = [];
      if (reservationsResponse.data.success) {
        reservationsData = reservationsResponse.data.reservations || [];
      }

      
      if (reservationsData.length > 0) {
      }

      const maintenant = new Date();
      
      // ✅ CORRECTION: Inclure les réservations "en_attente" ET "confirmé"
      const coursAVenir = reservationsData.filter(r => {
        // ✅ Accepter "confirmé" ET "en_attente" pour les professeurs
        if (!['confirmé', 'en_attente'].includes(r.statut)) {
          return false;
        }
        
        let heureField = null;
        let dateCours = null;
        
        // Priorité à start_utc (fournie par backend depuis étape 3)
        if (r.start_utc) {
          heureField = 'start_utc';
          dateCours = new Date(r.start_utc);
        } else if (r.heure_debut) {
          heureField = 'heure_debut';
          dateCours = new Date(r.date + 'T' + r.heure_debut);
        } else if (r.heure) {
          heureField = 'heure';
          dateCours = new Date(r.date + 'T' + r.heure);
        } else if (r.start_time) {
          heureField = 'start_time';
          dateCours = new Date(r.date + 'T' + r.start_time);
        } else {
          return false;
        }
        
        // Considérer le cours comme "à venir" jusqu'à son heure de FIN
let dateFinCours = null;
if (r.end_utc) {
  dateFinCours = new Date(r.end_utc);
} else if (r.heure_fin) {
  dateFinCours = new Date(r.date + 'T' + r.heure_fin);
} else {
  // Si pas d'heure de fin, ajouter 30 min à l'heure de début
  dateFinCours = new Date(dateCours.getTime() + 30 * 60 * 1000);
}

return !isNaN(dateCours.getTime()) && dateFinCours > maintenant;
 
      });
      
      const coursFinis = reservationsData.filter(r => {
        if (r.statut !== 'confirmé') return false; // Seuls les confirmés comptent comme "finis"
        
        let dateCours = null;
        if (r.start_utc) {
          dateCours = new Date(r.start_utc);
        } else if (r.heure_debut) {
          dateCours = new Date(r.date + 'T' + r.heure_debut);
        } else if (r.heure) {
          dateCours = new Date(r.date + 'T' + r.heure);
        } else if (r.start_time) {
          dateCours = new Date(r.date + 'T' + r.start_time);
        }
        
        return dateCours && !isNaN(dateCours.getTime()) && dateCours <= maintenant;
      });
      
      
      setProchainsCours(coursAVenir.slice(0, 5));

      // 👥 Liste unique des élèves rencontrés (confirmés, passés + à venir) → la fiche
      // de progression reste accessible APRÈS le cours, pas seulement quand un cours est à venir.
      const elevesMap = new Map();
      reservationsData
        .filter(r => r.statut === 'confirmé' && r.eleves)
        .forEach(r => {
          const eleveId = r.eleves.created_by;
          if (!eleveId) return;
          const dateCours = r.start_utc ? new Date(r.start_utc)
            : (r.date && (r.heure_debut || r.heure || r.start_time))
              ? new Date(r.date + 'T' + (r.heure_debut || r.heure || r.start_time))
              : null;
          const existing = elevesMap.get(eleveId);
          const nom = r.eleves.nom || r.eleves.prenom || 'Élève';
          if (!existing) {
            elevesMap.set(eleveId, { eleveId, nom, dernierCours: dateCours });
          } else if (dateCours && (!existing.dernierCours || dateCours > existing.dernierCours)) {
            existing.dernierCours = dateCours;
          }
        });
      const elevesListe = Array.from(elevesMap.values())
        .sort((a, b) => (b.dernierCours?.getTime() || 0) - (a.dernierCours?.getTime() || 0));
      setMesEleves(elevesListe);

      // 👁 Observation pédagogique : réservé admin. On tente l'appel ; 403 => pas admin
      // => la carte reste masquée. Rafraîchi à chaque cycle (statut "joignable maintenant").
      try {
        const obsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/agora/observable-courses`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        setObservableCourses(obsRes.data?.success ? (obsRes.data.cours || []) : null);
      } catch (e) {
        setObservableCourses(null); // 403 (non-admin) ou erreur => ne rien afficher
      }

      setStats({
        totalCours: reservationsData.filter(r => r.statut === 'confirmé').length,
        coursAVenir: coursAVenir.length, // ✅ Inclut maintenant les "en_attente"
        coursEnAttente: reservationsData.filter(r => r.statut === 'en_attente').length, // 🔔 badge "Demandes"
        coursConfirmesAVenir: coursAVenir.filter(r => r.statut === 'confirmé').length, // 🔔 badge "Mes Cours"
        coursFinis: coursFinis.length,
        evaluations: profData.nombre_avis || 0,
        noteMoyenne: profData.rating_moyen || 0,
        revenus: coursFinis.length * (profData.prix_30min || 15) // ✅ Même calcul que l'ancien
      });

      // Charger les évaluations
      try {
        const reviewsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/reviews/prof/${profData.id}?limit=3`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });

        if (reviewsResponse.data.success) {
          setRecentEvaluations(reviewsResponse.data.reviews || []);
        }
      } catch (reviewError) {
        setRecentEvaluations([]);
      }

      await loadPayoutData(accessToken);
      
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = () => {
    if (profProfile?.nom && profProfile?.prenom) {
      return `${profProfile.nom.charAt(0)}${profProfile.prenom.charAt(0)}`.toUpperCase();
    }
    return user?.email?.slice(0, 2).toUpperCase() || 'PR';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleProfileUpdate = (updatedData) => {
    setProfProfile(prev => ({ ...prev, ...updatedData }));
  };

  const handlePhotoUpdate = (newPhotoUrl) => {
    setProfProfile(prev => ({ ...prev, photo_url: newPhotoUrl }));
  };

  const shouldShowBankingSection = () => {
    return profProfile?.is_validated && (!profProfile?.rib_maroc && !profProfile?.iban || !profProfile?.account_holder);
  };

  // ✅ MÉMOISATION: Évite les re-calculs constants qui causent le clignotement
  const upcomingCoursesFormatted = React.useMemo(() => {
  const now = currentTime;
  
  return prochainsCours.map(cours => {
    const courseTime = cours.heure_debut || cours.heure || cours.start_time || '00:00';
    // Priorité à start_utc (enrichi côté backend via enrichWithUTC) — sinon la comparaison
    // interprète courseTime dans la TZ du navigateur au lieu de la TZ du prof, ce qui décale
    // "Rejoindre" d'~1h dès que le navigateur du prof n'est pas sur Africa/Casablanca.
    const courseDateTime = cours.start_utc ? new Date(cours.start_utc) : new Date(`${cours.date}T${courseTime}`);
    const diffMinutes = (courseDateTime - now) / (1000 * 60);
    const isAccessible = diffMinutes <= 30 && diffMinutes > -30;
    const videoLink = cours.jitsi_url || `https://www.firstarabic.com/cours/firstarabic-${cours.id}?reservationId=${cours.id}`;
    
    return {
      id: cours.id,
      time: courseTime.substring(0, 5), // Enlève les secondes "10:30:00" → "10:30"
      studentName: cours.eleves?.nom || cours.eleves?.prenom || 'Élève',
      courseInfo: `${cours.duree_minutes || 30} min`,
      status: cours.statut,
      statusLabel: cours.statut === 'en_attente' ? 'En attente' : 'Confirmé',
      isAccessible,
      videoLink,
      diffMinutes
    };
  });
}, [prochainsCours, currentTime]);
  if (loading) {
    return (
      <div className="modern-dashboard">
        <div className="dashboard-container">
          <div className="glass-card">
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
              <p>Chargement de votre dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!session) return <p>Veuillez vous connecter</p>;

  return (
    <div className="modern-dashboard">
      {/* ── FICHE ÉLÈVE PROGRESSION ── */}
      {ficheEleveOpen && ficheEleveData && (
        <FicheEleveProgression
          eleveId={ficheEleveData.eleveId}
          eleveName={ficheEleveData.eleveName}
          token={token}
          onClose={() => { setFicheEleveOpen(false); setFicheEleveData(null); }}
        />
      )}

      {/* Guide d'onboarding pour les nouveaux profs */}
      <OnboardingGuide theme="prof" 
        steps={profOnboardingSteps} 
        storageKey="prof-onboarding-completed"
      />
      <div className="dashboard-container">
       {/* Bannière RIB manquant */}
        {shouldShowBankingSection() && (
          <div className="banking-alert-banner">
            <div className="banner-content">
              <div className="banner-icon"><CreditCard size={22} strokeWidth={1.9} /></div>
              <div className="banner-text">
                <h3>Complétez vos informations bancaires</h3>
                <p>Pour recevoir vos paiements, ajoutez votre IBAN</p>
              </div>
              <div className="banner-action">
                <button 
                  className="cta-button-small"
                  onClick={() => handleTabChange('banking')}
                >
                  Ajouter mon IBAN
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header moderne avec upload de photo */}
        <div className={`modern-header ${isVisible ? 'visible' : ''}`}>
          <div className="header-content">
            <PhotoUploadComponent 
              profProfile={profProfile} 
              token={token}
              onPhotoUpdate={handlePhotoUpdate}
            />
            
            <div className="welcome-section">
              <h1>Bienvenue, {profProfile?.nom || 'Professeur'}</h1>
              <p>
                Vous avez {stats.coursAVenir} cours à venir • 
                Dernière connexion : {new Date().toLocaleDateString('fr-FR')}
              </p>
              {profProfile?.is_validated ? (
                <div className="validation-status">
                  <span className="validation-badge">Profil validé</span>
                  {(profProfile?.rib_maroc || profProfile?.iban) && profProfile?.account_holder ? (
                    <span className="banking-badge">RIB configuré</span>
                  ) : (
                    <span className="banking-badge incomplete">RIB manquant</span>
                  )}
                </div>
              ) : (
                <span className="validation-badge pending">En attente</span>
              )}
            </div>
            
            <div className="header-stats">
              <div className="header-stat-item" onClick={() => setActiveTab('evaluations')}>
                <span className="stat-number">
                  {stats.noteMoyenne > 0 ? stats.noteMoyenne.toFixed(1) : '0.0'}
                </span>
                <div className="stat-label">Note</div>
              </div>
              
              <div className="header-stat-item">
                <span className="stat-number">{stats.totalCours}</span>
                <div className="stat-label">Cours donnés</div>
              </div>
              
              <div className="header-stat-item" onClick={() => setActiveTab('banking')}>
                <span className="stat-number">{formatCurrency(stats.revenus)}</span>
                <div className="stat-label">Revenus</div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation par onglets - AVEC ONGLET RÉSERVATIONS AJOUTÉ */}
        <div className="glass-card">
         <div data-tour="prof-tabs" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}> 
            {[
              { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
              { id: 'cours', label: 'Mes Cours', Icon: GraduationCap, count: stats.coursConfirmesAVenir },
              { id: 'reservations', label: 'Demandes de cours', Icon: ClipboardList, count: stats.coursEnAttente },
              { id: 'disponibilites', label: 'Disponibilités', Icon: CalendarDays },
              ...(profProfile?.is_validated ? [{ id: 'banking', label: 'Informations Bancaires', Icon: Landmark, attention: !profProfile?.rib_maroc && !profProfile?.iban }] : []),
              { id: 'profil', label: 'Mon Profil', Icon: User }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '12px',
                  background: activeTab === tab.id 
                    ? 'linear-gradient(135deg, #065f46 0%, #047857 100%)' 
                    : tab.attention 
                      ? 'linear-gradient(135deg, #065f46 0%, #047857 100%)'
                      : 'rgba(255, 255, 255, 0.5)',
                  color: activeTab === tab.id || tab.attention ? 'white' : '#64748b',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '0.875rem',
                  position: 'relative'
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><tab.Icon size={16} strokeWidth={1.9} />{tab.label}{tab.count > 0 && <span style={{ background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700, minWidth: '18px', height: '18px', borderRadius: '9px', padding: '0 5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>{tab.count}</span>}</span>
                {tab.attention && <span className="alert-dot" style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '8px',
                  height: '8px',
                  background: '#ef4444',
                  borderRadius: '50%',
                  border: '2px solid white'
                }}></span>}
              </button>
            ))}
          </div>
        </div>

        {/* Contenu selon l'onglet actif */}
        {activeTab === 'dashboard' && (
          <div className="dashboard-grid-stable">
            {/* Colonne principale */}
            <div className="main-content">
              {/* Card revenus avec vraies données */}
              <div data-tour="prof-revenus" className="glass-card">
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon">📊</span>
                    Revenus & Performance
                  </h3>
                </div>
                
                <div className="revenue-section">
                  <div className="revenue-chart-container">
                    <div className="chart-animated-line"></div>
                    <div className="chart-points">
                      <div className="chart-point"></div>
                      <div className="chart-point"></div>
                      <div className="chart-point"></div>
                      <div className="chart-point"></div>
                    </div>
                  </div>
                  
                  <div className="revenue-stats-grid">
                    <div className="revenue-stat-item">
                      <div className="revenue-amount">{formatCurrency(stats.revenus)}</div>
                      <div className="revenue-label">Total revenus</div>
                      <div className="revenue-change positive">
                        {stats.coursFinis > 0 ? `${stats.coursFinis} cours` : 'Nouveau'}
                      </div>
                    </div>
                    <div className="revenue-stat-item">
                      <div className="revenue-amount">{formatCurrency(stats.revenus * 0.7)}</div>
                      <div className="revenue-label">Estimation précédente</div>
                    </div>
                    <div className="revenue-stat-item">
                      <div className="revenue-amount">{formatCurrency(stats.revenus * 1.2)}</div>
                      <div className="revenue-label">Objectif</div>
                      <div className="revenue-change positive">+20% ↗</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistiques détaillées avec vraies données */}
              <div data-tour="prof-stats" className="glass-card">
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon">📈</span>
                    Statistiques Détaillées
                  </h3>
                </div>
                
                <div className="stats-grid">
                  <div className="animated-stat-card">
                    <span className="number">{stats.totalCours}</span>
                    <div className="label">Cours confirmés</div>
                  </div>
                  <div className="animated-stat-card">
                    <span className="number">{stats.coursAVenir}</span>
                    <div className="label">Cours à venir</div>
                  </div>
                  <div className="animated-stat-card">
                    <span className="number">{stats.evaluations}</span>
                    <div className="label">Avis reçus</div>
                  </div>
                  <div className="animated-stat-card">
                    <span className="number">{stats.coursFinis}</span>
                    <div className="label">Cours terminés</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - STABLE ET TOUJOURS AFFICHÉE */}
            <div className="sidebar-content-stable">
              {/* Prochains cours - SECTION STABLE */}
              <div data-tour="prof-prochains" className="glass-card">
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon">🕐</span>
                    Prochains Cours ({upcomingCoursesFormatted.length})
                  </h3>
                </div>
                
                <div className="timeline-container-stable">
                  {upcomingCoursesFormatted.length > 0 ? (
                  upcomingCoursesFormatted.map((course) => (
  <div key={course.id} className={`timeline-item ${course.status}`}>
    <div className="timeline-time">{course.time}</div>
    <div className="timeline-content">
      <div className="student-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {course.studentName}
        <button
          onClick={() => {
            const cours = prochainsCours.find(c => c.id === course.id);
            setFicheEleveData({
              eleveId: cours?.eleves?.created_by,
              eleveName: cours?.eleves?.nom || cours?.eleves?.prenom || course.studentName,
            });
            setFicheEleveOpen(true);
          }}
          style={{
            padding: '5px 12px', borderRadius: 8, border: '1px solid #a7f3d0',
            background: '#E1F5EE', color: '#065f46',
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          }}
        >
          <TrendingUp size={14} strokeWidth={2} />Fiche de progression
        </button>
      </div>
      <div className="course-info">{course.courseInfo}</div>
      <div className="course-status-label">{course.statusLabel}</div>
      {course.status === 'confirmé' && course.isAccessible && (
        <a 
          href={course.videoLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            marginTop: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#10b981',
            color: 'white',
            borderRadius: '8px',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: 'bold'
          }}
        >
          🎥 Rejoindre
        </a>
      )}
      {course.status === 'confirmé' && !course.isAccessible && course.diffMinutes > 15 && (
        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
          Lien disponible 15 min avant
        </p>
      )}
    </div>
    <div className={`timeline-status ${course.status}`}></div>
  </div>
))
                  ) : (
                    <div className="empty-timeline">
                      <div className="empty-icon">📅</div>
                      <p>Aucun cours planifié</p>
                      <p className="empty-subtitle">
                        Les demandes de cours apparaîtront ici
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* 👁 Observation pédagogique (admin uniquement) */}
              {observableCourses && (
                <div className="glass-card">
                  <div className="card-header">
                    <h3 className="card-title">
                      <span className="card-icon">👁</span>
                      Observation pédagogique
                    </h3>
                  </div>
                  <div className="timeline-container-stable">
                    {observableCourses.length > 0 ? observableCourses.map((c) => (
                      <div key={c.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        gap: 10, flexWrap: 'wrap', padding: '10px 0',
                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                      }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{c.prof} · {c.eleve}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {c.start_utc ? formatDate(c.start_utc) : ''}
                            {c.joignable_maintenant ? ' · 🟢 en cours' : ''}
                          </div>
                        </div>
                        {c.joignable_maintenant ? (
                          <a
                            href={`/cours/${encodeURIComponent(c.canal)}?observer=1&reservationId=${c.id}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{
                              padding: '6px 14px', borderRadius: 8, background: '#0b1f17',
                              color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            👁 Rejoindre en observateur
                          </a>
                        ) : (
                          <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>Bientôt</span>
                        )}
                      </div>
                    )) : (
                      <div className="empty-timeline">
                        <div className="empty-icon">👁</div>
                        <p>Aucun cours à observer pour le moment</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions rapides - SECTION STABLE */}
              <div className="glass-card">
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon">⚡</span>
                    Actions Rapides
                  </h3>
                </div>
                
                <div className="quick-actions-grid-stable">
                  <button 
                    className="modern-action-btn primary"
                    onClick={() => setActiveTab('reservations')}
                  >
                    <span>📋</span> 
                    <span>Demandes</span>
                  </button>
                  <button 
                    className="modern-action-btn secondary"
                    onClick={() => setActiveTab('disponibilites')}
                  >
                    <span>🗓️</span>
                    <span>Planning</span>
                  </button>
                  <button className="modern-action-btn secondary">
                    <span>📊</span>
                    <span>Analytics</span>
                  </button>
                  <button 
                    className="modern-action-btn secondary"
                    onClick={() => setActiveTab('banking')}
                  >
                    <span>💰</span>
                    <span>Paiements</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
{activeTab === 'banking' && profProfile?.is_validated && (
  <div className="glass-card">
    <div className="banking-tab-header">
      <h2>🏦 Informations Bancaires & Paiements</h2>
      <p>Configurez vos coordonnées bancaires et votre compte Stripe pour recevoir vos paiements</p>
    </div>
 
    <BankingInfoSection
      profData={profProfile}
      onUpdate={handleProfileUpdate}
    />
  </div>
)}



        {/* Onglet Réservations - AJOUTÉ */}
        {activeTab === 'reservations' && (
          <div className="glass-card">
            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📋</span> Demandes de cours
            </h3>
            
            <ReservationsDashboard token={token} />
          </div>
        )}

        {/* Onglet Disponibilités */}
        {activeTab === 'disponibilites' && (
          <div className="glass-card">
            <AvailabilityManagement profId={profProfile?.id} token={token} />
          </div>
        )}

        {/* Autres onglets */}
        {activeTab === 'cours' && (
        <div className="glass-card">
  <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    <span>🎓</span> Mes Cours ({stats.totalCours} total)
  </h3>

  {/* 👥 Mes élèves — accès permanent à la fiche de progression (même après le cours) */}
  {mesEleves.length > 0 && (
    <div style={{ marginBottom: '2rem' }}>
      <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
        <span>👥</span> Mes élèves
        <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>
          — consultez leur progression à tout moment
        </span>
      </h4>
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {mesEleves.map((el) => (
          <div key={el.eleveId} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
            background: 'rgba(255,255,255,0.5)', padding: '0.85rem 1rem', borderRadius: 12,
          }}>
            <div>
              <strong>👨‍🎓 {el.nom}</strong>
              {el.dernierCours && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Dernier cours : {formatDate(el.dernierCours)}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                setFicheEleveData({ eleveId: el.eleveId, eleveName: el.nom });
                setFicheEleveOpen(true);
              }}
              style={{
                padding: '6px 14px', borderRadius: 8, border: '1px solid #a7f3d0',
                background: '#E1F5EE', color: '#065f46',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
              }}
            >
              <TrendingUp size={15} strokeWidth={2} />Fiche de progression
            </button>
          </div>
        ))}
      </div>
    </div>
  )}

  <MesCoursProf />
  
  {recentEvaluations.length > 0 && (
    <div style={{ marginTop: '2rem' }}>
      <h4>⭐ Évaluations récentes</h4>
      <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
        {recentEvaluations.map((review) => (
          <div key={review.id} style={{
            background: 'rgba(255, 255, 255, 0.5)',
            padding: '1rem',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <strong>👨‍🎓 {review.eleves?.nom || 'Élève'}</strong>
              <span>{'⭐'.repeat(review.note || 5)}</span>
            </div>
            <p>{review.commentaire}</p>
            <small style={{ color: '#64748b' }}>📅 {formatDate(review.created_at)}</small>
          </div>
        ))}
      </div>
    </div>
  )}
</div>
        )}

        {activeTab === 'profil' && (
          <div className="glass-card">
            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>👤</span> Mon Profil
            </h3>
            
            {profProfile && (
              <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.5)', padding: '1rem', borderRadius: '12px' }}>
                  <strong>Nom :</strong> {profProfile.nom} {profProfile.prenom}
                </div>
                <div style={{ background: 'rgba(255, 255, 255, 0.5)', padding: '1rem', borderRadius: '12px' }}>
                  <strong>Email :</strong> {profProfile.email}
                </div>
                <div style={{ background: 'rgba(255, 255, 255, 0.5)', padding: '1rem', borderRadius: '12px' }}>
                  <strong>Bio :</strong> {profProfile.bio || 'Aucune bio'}
                </div>
                <div style={{ background: 'rgba(255, 255, 255, 0.5)', padding: '1rem', borderRadius: '12px' }}>
                  {/* Prix masqués temporairement - tarif fixe FirstArabic */}
                </div>
              </div>
            )}
            
            <ProfilEditForm profProfile={profProfile} token={token} onSaved={(updated) => setProfProfile(prev => ({...prev, ...updated}))} />
          </div>
        )}
        
        {/* Footer avec logout */}
        <div className="glass-card" style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4>💡 Conseils</h4>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1rem' }}>
                <li>Soyez ponctuel pour vos cours</li>
                <li>Encouragez vos élèves à laisser des avis</li>
                {shouldShowBankingSection() && (
                  <li style={{ color: '#065f46', fontWeight: '600' }}>⚠️ Ajoutez votre IBAN pour recevoir vos paiements</li>
                )}
              </ul>
            </div>
            
            <button 
              onClick={handleLogout} 
              style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              🚪 Se déconnecter
            </button>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .avatar-section {
          position: relative;
        }

        .modern-avatar-container {
          position: relative;
          display: inline-block;
        }

        .modern-avatar {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.3s ease;
          background: linear-gradient(135deg, #065f46 0%, #047857 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modern-avatar:hover {
          transform: scale(1.05);
        }

        .avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          top: 0;
          left: 0;
        }

        .avatar-initials {
          color: white;
          font-size: 1.5rem;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        .avatar-initials.hidden {
          display: none;
        }

        .avatar-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .modern-avatar-container:hover .avatar-overlay {
          opacity: 1;
        }

        .upload-icon {
          width: 24px;
          height: 24px;
          color: white;
        }

        .upload-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid transparent;
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .avatar-file-input {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }

        .online-indicator {
          position: absolute;
          bottom: 5px;
          right: 5px;
          width: 20px;
          height: 20px;
          background: #10B981;
          border: 3px solid white;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; transform: scale(1.1); }
        }

        /* STYLES STABLES POUR ÉVITER LE CLIGNOTEMENT */
        .dashboard-grid-stable {
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: 2rem;
          margin-bottom: 2rem;
          min-height: 500px;
        }

        .sidebar-content-stable {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          position: sticky;
          top: 2rem;
          height: fit-content;
        }

        .timeline-container-stable {
          min-height: 120px;
          padding: 1rem 0;
        }

        .quick-actions-grid-stable {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          min-height: 100px;
        }

        .modern-action-btn {
          padding: 0.75rem 0.5rem;
          border: none;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
          text-align: center;
        }

        .modern-action-btn.primary {
          background: linear-gradient(135deg, #065f46 0%, #047857 100%);
          color: white;
        }

        .modern-action-btn.secondary {
          background: rgba(255, 255, 255, 0.7);
          color: #374151;
        }

        .modern-action-btn:hover {
          transform: translateY(-1px);
        }

        .modern-action-btn.primary:hover {
          background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
        }

        .modern-action-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.9);
        }

        .empty-timeline {
          text-align: center;
          padding: 2rem 1rem;
          color: #64748b;
        }

        .empty-icon {
          font-size: 2rem;
          margin-bottom: 1rem;
        }

        .empty-subtitle {
          font-size: 0.875rem;
          margin-top: 0.5rem;
          opacity: 0.7;
        }

        /* Styles pour les statuts des cours */
        .timeline-item.en_attente {
          border-left: 3px solid #065f46;
          background: rgba(245, 158, 11, 0.05);
        }

        .timeline-item.confirmé {
          border-left: 3px solid #10b981;
          background: rgba(16, 185, 129, 0.05);
        }

        .timeline-item {
          padding: 1rem;
          margin-bottom: 0.75rem;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.3);
          border-left: 3px solid #e5e7eb;
        }

        .timeline-time {
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
        }

        .timeline-content {
          margin-top: 0.5rem;
        }

        .student-name {
          font-weight: 500;
          color: #111827;
        }

        .course-info {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .course-status-label {
          font-size: 0.75rem;
          font-weight: 500;
          margin-top: 4px;
          color: #64748b;
        }

        .timeline-status {
          display: none;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .dashboard-grid-stable {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          .sidebar-content-stable {
            position: static;
          }
        }
      `}</style>
    </div>
  );
};

// Constantes pour l'édition profil
const PROF_CATEGORIES = [
  { id: 'fusha', label: 'Arabe littéraire', emoji: '📖', sousoptions: ['Arabe littéraire — Général','Grammaire et syntaxe','Lecture et écriture','Arabe des affaires','Préparation examens','Culture et littérature'] },
  { id: 'coran', label: 'Coran & Tajwid', emoji: '🕌', sousoptions: ['Lecture du Coran','Tajwid (règles de récitation)','Mémorisation (Hifz)','Exégèse (Tafsir) — initiation'] },
  { id: 'dialectal', label: 'Arabe dialectal', emoji: '💬', sousoptions: ['Darija marocaine','Darija algérienne','Darija tunisienne','Darija libyenne','Arabe égyptien','Arabe levantin (Syrie, Liban, Palestine, Jordanie)','Arabe du Golfe','Arabe irakien','Arabe mauritanien (Hassaniya)'] },
  { id: 'amazighe', label: 'Amazighe / Tamazight', emoji: '⵿', sousoptions: ['Tarifit (Rif — nord Maroc)','Tachelhit (Souss — sud Maroc)','Tamazight central (Moyen Atlas)','Kabyle (Algérie)','Chaoui (Aurès — Algérie)','Mozabite (Ghardaïa — Algérie)','Touareg','Tamazight de Libye','Tamazight de Tunisie'] },
  { id: 'calligraphie', label: 'Calligraphie arabe', emoji: '✍️', sousoptions: ['Calligraphie — initiation','Calligraphie Naskh','Calligraphie Thuluth','Calligraphie Ruqah','Calligraphie Diwani'] },
];
const PROF_NIVEAUX_ELEVES = ['Enfants 5-10 ans','Enfants 11-15 ans','Adolescents','Adultes','Tous niveaux'];
const PROF_LANGUES_COMM = ['Français','Anglais','Arabe','Allemand','Espagnol','Italien','Néerlandais','Flamand','Portugais','Suédois','Danois','Norvégien','Russe','Turc'];
const PROF_PAYS = ['Maroc','Algérie','Tunisie','Égypte','Libye','Mauritanie','France','Belgique','Pays-Bas','Allemagne','Espagne','Italie','Suède','Autre'];


// ============================================================
// COMPOSANT StripeConnectSection
// À ajouter dans ModernProfDashboard.js
// - Coller ce composant juste avant la fonction ProfilEditForm
// - Modifier l'onglet banking (voir instructions en bas)
// ============================================================

function StripeConnectSection({ profProfile, token, onStatusUpdate }) {
  const API = process.env.REACT_APP_API_URL || '';
  const [stripeStatus, setStripeStatus] = React.useState(null); // null | 'not_created' | 'pending' | 'active'
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [generatingLink, setGeneratingLink] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  // Vérifier le statut au montage
  React.useEffect(() => {
    checkStatus();
    // Gérer le retour depuis l'onboarding Stripe
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'success') {
      setMsg('✅ Onboarding Stripe complété ! Votre compte est en cours de vérification.');
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
      // Re-vérifier le statut
      setTimeout(() => checkStatus(), 2000);
    } else if (params.get('stripe') === 'refresh') {
      setMsg('⚠️ Le lien a expiré. Cliquez sur "Continuer l\'onboarding" pour reprendre.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/profs/connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setStripeStatus(data.status); // 'not_created' | 'pending' | 'active'
        if (onStatusUpdate) onStatusUpdate(data);
      }
    } catch (e) {
      console.error('Erreur vérification statut Stripe:', e);
    } finally {
      setLoading(false);
    }
  };

  const createAccount = async () => {
    setCreating(true);
    setMsg('');
    try {
      const res = await fetch(`${API}/profs/connect/create-account`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setStripeStatus('pending');
        // Générer immédiatement le lien d'onboarding
        await generateOnboardingLink();
      } else {
        setMsg(`❌ ${data.error || 'Erreur création compte'}`);
      }
    } catch (e) {
      setMsg('❌ Erreur de connexion');
    } finally {
      setCreating(false);
    }
  };

  const generateOnboardingLink = async () => {
    setGeneratingLink(true);
    setMsg('');
    try {
      const res = await fetch(`${API}/profs/connect/onboarding-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.onboarding_url) {
        // Rediriger vers l'onboarding Stripe
        window.location.href = data.onboarding_url;
      } else {
        setMsg(`❌ ${data.error || 'Erreur génération lien'}`);
      }
    } catch (e) {
      setMsg('❌ Erreur de connexion');
    } finally {
      setGeneratingLink(false);
    }
  };

  const s = {
    container: {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
      border: '1.5px solid #a7f3d0',
      borderRadius: 12,
      padding: '24px',
      marginTop: 24
    },
    title: {
      fontSize: 15,
      fontWeight: 700,
      color: '#065f46',
      marginBottom: 6,
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    subtitle: {
      fontSize: 13,
      color: '#6b7280',
      marginBottom: 20,
      lineHeight: 1.5
    },
    statusBox: (color) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '12px 16px',
      borderRadius: 8,
      background: color === 'green' ? '#f0fff4' : color === 'yellow' ? '#fffbeb' : '#f8fafc',
      border: `1px solid ${color === 'green' ? '#9ae6b4' : color === 'yellow' ? '#fcd34d' : '#e2e8f0'}`,
      marginBottom: 16
    }),
    statusDot: (color) => ({
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: color === 'green' ? '#10b981' : color === 'yellow' ? '#f59e0b' : '#94a3b8',
      flexShrink: 0
    }),
    btn: (variant) => ({
      padding: '11px 22px',
      borderRadius: 8,
      border: 'none',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: 14,
      background: variant === 'primary'
        ? 'linear-gradient(135deg, #065f46, #047857)'
        : variant === 'secondary'
        ? 'rgba(6, 95, 70, 0.08)'
        : '#f1f5f9',
      color: variant === 'primary' ? '#fff' : variant === 'secondary' ? '#065f46' : '#64748b',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6
    }),
    stepsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 12,
      marginBottom: 20
    },
    step: (done) => ({
      textAlign: 'center',
      padding: '12px 8px',
      borderRadius: 8,
      background: done ? '#f0fff4' : '#f8fafc',
      border: `1px solid ${done ? '#9ae6b4' : '#e2e8f0'}`
    }),
    stepNum: (done) => ({
      width: 28,
      height: 28,
      borderRadius: '50%',
      background: done ? '#10b981' : '#e2e8f0',
      color: done ? '#fff' : '#94a3b8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 700,
      margin: '0 auto 6px'
    }),
    stepLabel: { fontSize: 12, color: '#6b7280', fontWeight: 500 }
  };

  if (loading) {
    return (
      <div style={s.container}>
        <div style={s.title}>💳 Paiements Stripe</div>
        <div style={{ color: '#94a3b8', fontSize: 13 }}>⏳ Vérification du statut...</div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <div style={s.title}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#065f46" strokeWidth="2.5">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
        Paiements via Stripe Connect
      </div>
      <div style={s.subtitle}>
        Recevez vos paiements directement sur votre compte bancaire via Stripe. 
        Aucun intermédiaire — vos revenus sont virés le 1er de chaque mois.
      </div>

      {/* Étapes visuelles */}
      <div style={s.stepsGrid}>
        <div style={s.step(stripeStatus !== 'not_created')}>
          <div style={s.stepNum(stripeStatus !== 'not_created')}>
            {stripeStatus !== 'not_created' ? '✓' : '1'}
          </div>
          <div style={s.stepLabel}>Créer le compte</div>
        </div>
        <div style={s.step(stripeStatus === 'active')}>
          <div style={s.stepNum(stripeStatus === 'active')}>
            {stripeStatus === 'active' ? '✓' : '2'}
          </div>
          <div style={s.stepLabel}>Vérifier l'identité</div>
        </div>
        <div style={s.step(stripeStatus === 'active')}>
          <div style={s.stepNum(stripeStatus === 'active')}>
            {stripeStatus === 'active' ? '✓' : '3'}
          </div>
          <div style={s.stepLabel}>Recevoir les paiements</div>
        </div>
      </div>

      {/* Statut actuel */}
      {stripeStatus === 'not_created' && (
        <>
          <div style={s.statusBox('gray')}>
            <div style={s.statusDot('gray')} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Compte non configuré</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Créez votre compte Stripe pour commencer à recevoir des paiements</div>
            </div>
          </div>
          <button
            onClick={createAccount}
            disabled={creating}
            style={s.btn('primary')}
          >
            {creating ? '⏳ Création...' : '🚀 Configurer mes paiements Stripe'}
          </button>
        </>
      )}

      {stripeStatus === 'pending' && (
        <>
          <div style={s.statusBox('yellow')}>
            <div style={s.statusDot('yellow')} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>Onboarding en cours</div>
              <div style={{ fontSize: 12, color: '#92400e' }}>Complétez la vérification d'identité pour activer les paiements</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={generateOnboardingLink}
              disabled={generatingLink}
              style={s.btn('primary')}
            >
              {generatingLink ? '⏳ Chargement...' : '📋 Continuer l\'onboarding Stripe'}
            </button>
            <button onClick={checkStatus} style={s.btn('secondary')}>
              🔄 Vérifier le statut
            </button>
          </div>
        </>
      )}

      {stripeStatus === 'active' && (
        <>
          <div style={s.statusBox('green')}>
            <div style={s.statusDot('green')} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#065f46' }}>✅ Compte Stripe actif</div>
              <div style={{ fontSize: 12, color: '#047857' }}>Vos paiements seront virés automatiquement sur votre compte bancaire</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', background: '#f8fafc', padding: '10px 14px', borderRadius: 8, marginTop: 4 }}>
            💡 Les virements sont déclenchés par l'équipe FirstArabic le 1er de chaque mois. 
            Vous recevrez une notification par email à chaque virement.
          </div>
        </>
      )}

      {/* Message d'état */}
      {msg && (
        <div style={{
          marginTop: 14,
          padding: '10px 14px',
          borderRadius: 8,
          background: msg.includes('✅') ? '#f0fff4' : msg.includes('⚠️') ? '#fffbeb' : '#fff5f5',
          color: msg.includes('✅') ? '#065f46' : msg.includes('⚠️') ? '#92400e' : '#c53030',
          border: `1px solid ${msg.includes('✅') ? '#9ae6b4' : msg.includes('⚠️') ? '#fcd34d' : '#feb2b2'}`,
          fontSize: 13
        }}>
          {msg}
        </div>
      )}
    </div>
  );
}


// Composant édition profil complet
// ═══ Étape 10 : listes de fuseaux horaires pour ProfilEditForm ═══
const TIMEZONE_COMMON = [
  { value: 'Africa/Casablanca', label: '🇲🇦 Casablanca (UTC+1)' },
  { value: 'Africa/Algiers', label: '🇩🇿 Alger (UTC+1)' },
  { value: 'Africa/Tunis', label: '🇹🇳 Tunis (UTC+1)' },
  { value: 'Africa/Cairo', label: '🇪🇬 Le Caire (UTC+3)' },
  { value: 'Asia/Riyadh', label: '🇸🇦 Riyad (UTC+3)' },
  { value: 'Asia/Dubai', label: '🇦🇪 Dubaï (UTC+4)' },
  { value: 'Asia/Amman', label: '🇯🇴 Amman (UTC+3)' },
  { value: 'Asia/Beirut', label: '🇱🇧 Beyrouth (UTC+3)' },
  { value: 'Europe/Paris', label: '🇫🇷 Paris (UTC+2)' },
  { value: 'Europe/London', label: '🇬🇧 Londres (UTC+1)' },
  { value: 'Europe/Brussels', label: '🇧🇪 Bruxelles (UTC+2)' },
  { value: 'Europe/Madrid', label: '🇪🇸 Madrid (UTC+2)' },
  { value: 'America/New_York', label: '🇺🇸 New York (UTC-4)' },
  { value: 'America/Los_Angeles', label: '🇺🇸 Los Angeles (UTC-7)' },
  { value: 'America/Toronto', label: '🇨🇦 Montréal/Toronto (UTC-4)' },
];

// Liste IANA complète (fallback) - utilise Intl si dispo, sinon liste réduite
const TIMEZONE_ALL = (() => {
  try {
    if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch {}
  return [
    'Africa/Casablanca', 'Africa/Algiers', 'Africa/Tunis', 'Africa/Cairo', 'Africa/Nairobi',
    'Asia/Riyadh', 'Asia/Dubai', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Karachi', 'Asia/Amman', 'Asia/Beirut',
    'Europe/Paris', 'Europe/London', 'Europe/Brussels', 'Europe/Madrid', 'Europe/Berlin', 'Europe/Istanbul',
    'America/New_York', 'America/Los_Angeles', 'America/Chicago', 'America/Toronto', 'America/Sao_Paulo',
    'Australia/Sydney', 'Pacific/Auckland', 'UTC'
  ];
})();

function ProfilEditForm({ profProfile, token, onSaved }) {
  const API = process.env.REACT_APP_API_URL || '';
  const [form, setForm] = React.useState({
    nom: profProfile?.nom || '',
    prenom: profProfile?.prenom || '',
    bio: profProfile?.bio || '',
    telephone: profProfile?.telephone || '',
    experience_annees: profProfile?.experience_annees || 1,
    pays_origine: profProfile?.pays_origine || 'Maroc',
    specialites: profProfile?.specialites || [],
    langues_communication: profProfile?.langues_communication || ['Français'],
    niveau_eleves: profProfile?.niveau_eleves || [],
    timezone: profProfile?.timezone || 'Africa/Casablanca',
  });
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const toggle = (field, val) => setForm(p => ({
    ...p,
    [field]: p[field].includes(val) ? p[field].filter(x => x !== val) : [...p[field], val]
  }));

  const handleSave = async () => {
    if (!form.nom.trim()) { setMsg('❌ Le nom est obligatoire'); return; }
    if (!form.bio.trim() || form.bio.length < 20) { setMsg('❌ La bio doit contenir au moins 20 caractères'); return; }
    if (form.specialites.length === 0) { setMsg('❌ Sélectionnez au moins une spécialité'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/profs/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setMsg('✅ Profil mis à jour avec succès');
        onSaved(data.prof);
        setTimeout(() => setMsg(''), 4000);
      } else {
        setMsg(`❌ ${data.error || 'Erreur lors de la sauvegarde'}`);
      }
    } catch(e) {
      setMsg('❌ Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const inp = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 14, boxSizing: 'border-box', background: '#fff', marginTop: 6 };
  const lbl = { fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginTop: 16 };
  const tagBtn = (active) => ({ padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${active ? '#065f46' : '#e2e8f0'}`, background: active ? '#065f46' : '#fff', color: active ? '#fff' : '#4a5568', fontSize: 13, cursor: 'pointer', fontWeight: active ? 600 : 400, margin: '4px 4px 0 0' });
  const secTitle = { fontSize: 14, fontWeight: 700, color: '#065f46', marginTop: 24, marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #d1fae5' };

  return (
    <div>
      {/* Infos de base */}
      <div style={secTitle}>✏️ Informations personnelles</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={lbl}>Prénom *</label><input style={inp} value={form.prenom} onChange={e => setForm(p => ({...p, prenom: e.target.value}))} placeholder="Votre prénom" /></div>
        <div><label style={lbl}>Nom *</label><input style={inp} value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} placeholder="Votre nom" /></div>
      </div>
      <div><label style={lbl}>Téléphone WhatsApp</label><input style={inp} value={form.telephone} onChange={e => setForm(p => ({...p, telephone: e.target.value}))} placeholder="+212 6 12 34 56 78" /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Années d'expérience</label>
          <select style={inp} value={form.experience_annees} onChange={e => setForm(p => ({...p, experience_annees: Number(e.target.value)}))}>
            {[1,2,3,4,5,6,7,8,9,10,15,20].map(n => <option key={n} value={n}>{n}+ an{n > 1 ? 's' : ''}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Pays d'origine</label>
          <select style={inp} value={form.pays_origine} onChange={e => setForm(p => ({...p, pays_origine: e.target.value}))}>
            {PROF_PAYS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label style={lbl}>Bio * <span style={{fontWeight:400, color:'#6b7280'}}>({form.bio.length}/500)</span></label>
        <textarea style={{...inp, minHeight: 100, resize: 'vertical'}} value={form.bio} onChange={e => setForm(p => ({...p, bio: e.target.value}))} placeholder="Présentez-vous : votre expérience, votre approche pédagogique..." />
      </div>

      {/* Spécialisations */}
      <div style={secTitle}>📚 Ce que vous enseignez *</div>
      {PROF_CATEGORIES.map(cat => (
        <div key={cat.id} style={{ marginBottom: 12, background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{cat.emoji} {cat.label}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {cat.sousoptions.map(opt => (
              <button key={opt} type="button" style={tagBtn(form.specialites.includes(opt))} onClick={() => toggle('specialites', opt)}>{opt}</button>
            ))}
          </div>
        </div>
      ))}
      <div style={{ fontSize: 12, color: form.specialites.length === 0 ? '#e53e3e' : '#38a169', marginBottom: 8 }}>
        {form.specialites.length === 0 ? '⚠️ Sélectionnez au moins 1 spécialité' : `✅ ${form.specialites.length} spécialité(s)`}
      </div>

      {/* Élèves cibles */}
      <div style={secTitle}>🎓 Vos élèves cibles</div>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {PROF_NIVEAUX_ELEVES.map(n => (
          <button key={n} type="button" style={tagBtn(form.niveau_eleves.includes(n))} onClick={() => toggle('niveau_eleves', n)}>{n}</button>
        ))}
      </div>

      {/* Langues de communication */}
      <div style={secTitle}>🌍 Langues de communication</div>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {PROF_LANGUES_COMM.map(l => (
          <button key={l} type="button" style={tagBtn(form.langues_communication.includes(l))} onClick={() => toggle('langues_communication', l)}>{l}</button>
        ))}
      </div>

      {/* Message */}
      {msg && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginTop: 16, background: msg.includes('✅') ? '#f0fff4' : '#fff5f5', color: msg.includes('✅') ? '#065f46' : '#c53030', border: `1px solid ${msg.includes('✅') ? '#9ae6b4' : '#feb2b2'}`, fontSize: 13 }}>
          {msg}
        </div>
      )}

      {/* Bouton */}
      {/* ═══ Fuseau horaire (étape 10 timezone) ═══ */}
      <div>
        <label style={lbl}>🌍 Fuseau horaire *</label>
        <select style={inp} value={
          TIMEZONE_COMMON.some(tz => tz.value === form.timezone) ? form.timezone : 'AUTRE'
        } onChange={e => {
          const v = e.target.value;
          if (v !== 'AUTRE') setForm(p => ({ ...p, timezone: v }));
          else setForm(p => ({ ...p, timezone: p.timezone && !TIMEZONE_COMMON.some(t => t.value === p.timezone) ? p.timezone : '' }));
        }}>
          {TIMEZONE_COMMON.map(tz => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
          <option value="AUTRE">── Autre fuseau ──</option>
        </select>
        {!TIMEZONE_COMMON.some(tz => tz.value === form.timezone) && (
          <div style={{ marginTop: 8 }}>
            <label style={{ ...lbl, marginTop: 8 }}>Fuseau spécifique (IANA)</label>
            <select style={inp} value={form.timezone || ''} onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))}>
              <option value="">-- Sélectionnez --</option>
              {TIMEZONE_ALL.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        )}
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
          C'est l'heure dans laquelle vous saisissez vos disponibilités. Les élèves verront vos créneaux convertis dans leur propre fuseau.
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} style={{ marginTop: 20, padding: '12px 28px', borderRadius: 8, border: 'none', cursor: saving ? 'not-allowed' : 'pointer', background: saving ? '#94a3b8' : 'linear-gradient(135deg, #065f46, #047857)', color: '#fff', fontWeight: 600, fontSize: 14 }}>
        {saving ? '⏳ Enregistrement...' : '💾 Enregistrer les modifications'}
      </button>
    </div>
  );
}

export default ModernProfDashboard;
